import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { ShieldAlert, Search, Trash2, Loader2, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PHRASE = 'RESET GUARDIANSFIT';

export default function SystemResetPage() {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [keepIds, setKeepIds] = useState(new Set());
  const [phrase, setPhrase] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState(null);

  const runSimulation = async () => {
    setLoading(true);
    setResult(null);
    try {
      const res = await axios.post(`${API}/admin/system-reset`, { dry_run: true, keep_emails: [] });
      setPreview(res.data);
      setKeepIds(new Set());
    } catch (e) {
      toast.error('Error en la simulación');
    } finally {
      setLoading(false);
    }
  };

  const toggleKeep = (id) => {
    setKeepIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const willDelete = preview ? preview.clients.filter((c) => !keepIds.has(c.id)) : [];

  const executeReset = async () => {
    setExecuting(true);
    setConfirmOpen(false);
    try {
      const keepEmails = preview.clients.filter((c) => keepIds.has(c.id)).map((c) => c.email);
      const res = await axios.post(`${API}/admin/system-reset`, {
        dry_run: false,
        keep_emails: keepEmails,
        confirmation_phrase: phrase,
      });
      setResult(res.data);
      setPreview(null);
      setPhrase('');
      toast.success(res.data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error ejecutando el reset');
    } finally {
      setExecuting(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-4xl" data-testid="system-reset-page">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Trash2 className="h-6 w-6 text-red-400" /> Reset de producción
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Limpia los clientes de prueba y todos sus datos para dejar la app lista para usuarios reales
          </p>
        </div>

        <Card className="border-red-500/40 bg-red-950/10">
          <CardContent className="p-4 flex gap-3 items-start">
            <ShieldAlert className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-zinc-300 space-y-1">
              <p className="font-bold text-red-300">Qué hace y qué NO hace</p>
              <p>✅ Borra: cuentas de clientes (las que tú elijas), sus chats, registros de sesión, progreso, notificaciones y pagos de prueba.</p>
              <p>🛡️ NUNCA toca: tu cuenta admin, entrenadores, la librería de ejercicios, planes, macros, píldoras ni cursos.</p>
              <p>💾 Antes de borrar guarda una copia de seguridad automática de los usuarios.</p>
            </div>
          </CardContent>
        </Card>

        {/* PASO 1: Simulación */}
        {!result && (
          <Button onClick={runSimulation} disabled={loading} variant="outline" className="border-amber-500/50 text-amber-300" data-testid="reset-simulate-btn">
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
            Paso 1 · Ejecutar simulación (no borra nada)
          </Button>
        )}

        {/* PASO 2: Revisión */}
        {preview && (
          <Card data-testid="reset-preview">
            <CardContent className="p-5 space-y-4">
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3">
                  <p className="text-2xl font-extrabold text-red-400" data-testid="reset-delete-count">{willDelete.length}</p>
                  <p className="text-xs text-muted-foreground">clientes a borrar</p>
                </div>
                <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3">
                  <p className="text-2xl font-extrabold text-emerald-400">{keepIds.size}</p>
                  <p className="text-xs text-muted-foreground">conservados</p>
                </div>
                <div className="rounded-lg border border-border p-3">
                  <p className="text-2xl font-extrabold text-foreground">{preview.total_related}</p>
                  <p className="text-xs text-muted-foreground">documentos asociados</p>
                </div>
              </div>

              <p className="text-xs text-muted-foreground">
                Paso 2 · Marca la casilla de los clientes que quieras <strong className="text-emerald-400">CONSERVAR</strong> (ej: clientes reales que ya pagan):
              </p>

              <div className="max-h-80 overflow-y-auto space-y-1 border border-border rounded-lg p-2">
                {preview.clients.map((c) => (
                  <label
                    key={c.id}
                    className={`flex items-center gap-3 px-3 py-2 rounded-md cursor-pointer transition-colors ${
                      keepIds.has(c.id) ? 'bg-emerald-500/10 border border-emerald-500/30' : 'hover:bg-zinc-800/50'
                    }`}
                    data-testid={`reset-client-row-${c.id}`}
                  >
                    <Checkbox
                      checked={keepIds.has(c.id)}
                      onCheckedChange={() => toggleKeep(c.id)}
                      data-testid={`reset-keep-${c.id}`}
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">
                        {c.name}
                        {c.active_subscription && (
                          <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 border border-amber-500/40 text-amber-300">
                            ⚠ suscripción activa
                          </span>
                        )}
                      </p>
                      <p className="text-[11px] text-muted-foreground truncate">
                        {c.email} · {c.acquisition_source || 'origen desconocido'} {c.tier ? `· ${c.tier}` : ''}
                      </p>
                    </div>
                    <span className={`text-[11px] font-bold flex-shrink-0 ${keepIds.has(c.id) ? 'text-emerald-400' : 'text-red-400'}`}>
                      {keepIds.has(c.id) ? 'SE CONSERVA' : 'SE BORRA'}
                    </span>
                  </label>
                ))}
              </div>

              {/* PASO 3: Confirmación */}
              <div className="border-t border-border pt-4 space-y-3">
                <p className="text-xs text-muted-foreground">
                  Paso 3 · Para ejecutar el borrado REAL escribe exactamente: <code className="text-red-300 font-bold">{PHRASE}</code>
                </p>
                <div className="flex gap-3">
                  <Input
                    value={phrase}
                    onChange={(e) => setPhrase(e.target.value)}
                    placeholder={PHRASE}
                    className="max-w-xs"
                    data-testid="reset-phrase-input"
                  />
                  <Button
                    variant="destructive"
                    disabled={phrase.trim().toUpperCase() !== PHRASE || executing || willDelete.length === 0}
                    onClick={() => setConfirmOpen(true)}
                    data-testid="reset-execute-btn"
                  >
                    {executing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                    Borrar {willDelete.length} clientes
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Resultado */}
        {result && (
          <Card className="border-emerald-500/40 bg-emerald-950/10" data-testid="reset-result">
            <CardContent className="p-5 space-y-2">
              <p className="font-bold text-emerald-300 flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5" /> {result.message}
              </p>
              <p className="text-sm text-muted-foreground">
                {result.total_deleted} documentos eliminados en total · {result.clients_kept} clientes conservados · Copia de seguridad: <code className="text-xs">{result.backup_id}</code>
              </p>
              <Button variant="outline" size="sm" onClick={runSimulation} className="mt-2">
                Ver estado actual
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="reset-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-400">⚠ Última confirmación</AlertDialogTitle>
            <AlertDialogDescription>
              Vas a borrar <strong>{willDelete.length} clientes</strong> y todos sus datos ({preview?.total_related} documentos asociados).
              Se guardará copia de seguridad, pero esta acción no tiene botón de deshacer. ¿Seguro?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={executeReset} className="bg-red-600 hover:bg-red-500" data-testid="reset-confirm-final">
              Sí, borrar definitivamente
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}
